/**
 * R6 — CustomerAutoProfile.jsx
 *
 * Compact "Profilo automatico" collapsible card shown above NoteClienteRiquadro
 * in the DX column. Displays derived metrics (win rate, total value, preferred
 * materials, geo region).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { api } from "../lib/api";

function Chip({ label, value, tone = "default", testId }) {
  const toneStyle = {
    default: { bg: "var(--bg-subtle)", color: "var(--text-secondary)" },
    blue: { bg: "#DBEAFE", color: "#1D4ED8" },
    green: { bg: "#DCFCE7", color: "#15803D" },
    yellow: { bg: "#FEF3C7", color: "#B45309" },
    red: { bg: "#FEE2E2", color: "#B91C1C" },
  }[tone];
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{ background: toneStyle.bg, color: toneStyle.color }}
      data-testid={testId}
    >
      {label && <span className="opacity-70">{label}</span>}
      <span className="font-semibold">{value}</span>
    </span>
  );
}

export default function CustomerAutoProfile({ emailDomain }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (force = false) => {
      if (!emailDomain) return;
      setLoading(true);
      try {
        const d = await api.autoProfile(emailDomain, force);
        setData(d);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [emailDomain]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  if (!emailDomain) return null;

  const winToneFromRate = (r) => {
    if (r == null) return "default";
    if (r >= 40) return "green";
    if (r >= 20) return "yellow";
    return "red";
  };

  return (
    <section
      className="rounded-md mb-2"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
      }}
      data-testid="customer-auto-profile"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-2.5 py-1.5"
        data-testid="auto-profile-toggle"
      >
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold"
              style={{ color: "var(--text-secondary)" }}>
          <Sparkles className="h-3 w-3"
                    style={{ color: "var(--accent-primary)" }} />
          Profilo automatico
          {loading && (
            <Loader2 className="h-3 w-3 animate-spin"
                     style={{ color: "var(--text-muted)" }} />
          )}
        </span>
        {open ? (
          <ChevronUp className="h-3 w-3"
                     style={{ color: "var(--text-muted)" }} />
        ) : (
          <ChevronDown className="h-3 w-3"
                       style={{ color: "var(--text-muted)" }} />
        )}
      </button>

      {open && data && (
        <div className="px-2.5 pb-2.5 space-y-2"
             data-testid="auto-profile-body">
          <div className="flex flex-wrap gap-1">
            {data.geo_region && (
              <Chip label="Geo" value={data.geo_region} testId="ap-geo" />
            )}
            {data.sector && (
              <Chip label="Settore" value={data.sector} tone="blue"
                    testId="ap-sector" />
            )}
            {data.total_offers_count > 0 && (
              <Chip
                label="Offerte"
                value={data.total_offers_count}
                testId="ap-total-offers"
              />
            )}
            {data.win_rate_pct != null && (
              <Chip
                label="Win"
                value={`${data.win_rate_pct}%`}
                tone={winToneFromRate(data.win_rate_pct)}
                testId="ap-win-rate"
              />
            )}
            {data.total_offers_value_eur > 0 && (
              <Chip
                label="Valore"
                value={`€ ${Number(data.total_offers_value_eur).toLocaleString("it-IT")}`}
                tone="green"
                testId="ap-total-value"
              />
            )}
            {data.average_offer_size_eur != null && (
              <Chip
                label="Ø"
                value={`€ ${Number(data.average_offer_size_eur).toLocaleString("it-IT")}`}
                testId="ap-avg-size"
              />
            )}
            {data.payment_method_recurring && (
              <Chip
                label="Pag."
                value={data.payment_method_recurring}
                testId="ap-payment"
              />
            )}
          </div>

          {(data.preferred_materials?.length > 0 ||
            data.preferred_brands?.length > 0) && (
            <div className="text-[10px]"
                 style={{ color: "var(--text-muted)" }}>
              {data.preferred_materials?.length > 0 && (
                <div data-testid="ap-materials">
                  <span className="uppercase tracking-wide font-medium mr-1">
                    Materiali ricorrenti:
                  </span>
                  {data.preferred_materials.join(" · ")}
                </div>
              )}
              {data.preferred_brands?.length > 0 && (
                <div data-testid="ap-brands">
                  <span className="uppercase tracking-wide font-medium mr-1">
                    Marche ricorrenti:
                  </span>
                  {data.preferred_brands.join(" · ")}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-[10px]"
               style={{ color: "var(--text-muted)" }}>
            <span>
              {data.cached ? "Dati in cache" : "Ricalcolato ora"}
              {data.last_offer_date &&
                ` · ultima offerta ${String(data.last_offer_date).slice(0, 10)}`}
            </span>
            <button
              type="button"
              onClick={() => load(true)}
              className="inline-flex items-center gap-0.5 p-0.5 rounded hover:bg-slate-100"
              data-testid="auto-profile-refresh"
              aria-label="Aggiorna profilo"
              title="Ricalcola"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>

          {data.total_offers_count === 0 && (
            <div className="text-[11px] italic"
                 style={{ color: "var(--text-muted)" }}>
              Cliente nuovo — nessuna offerta storica in archivio.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
